// ─── Audit Log Viewer (superadmin only) ──────────────────────────────────────
//
// Reads /userAuditLogs/ — append-only mutation history of contacts + users.
// Per firestore.rules `userAuditLogs/` block, only superadmin can read; the
// page also self-gates in UI to avoid a wasteful 400.
//
// Filters: action type, performedBy (auth uid), subjectContactId. Expand row
// to see before/after JSON diff.

import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchRecentAuditLogs,
  type AuditLogRecord,
  type AuditAction,
} from '../services/auditService';

const ACTION_OPTIONS: { value: AuditAction | ''; label: string }[] = [
  { value: '',                          label: 'All actions' },
  { value: 'contact_created',           label: 'Contact created' },
  { value: 'contact_updated',           label: 'Contact updated' },
  { value: 'contact_deactivated',       label: 'Contact deactivated' },
  { value: 'contact_reactivated',       label: 'Contact reactivated' },
  { value: 'contact_merged',            label: 'Contact merged' },
  { value: 'contact_deleted',           label: 'Contact deleted' },
  { value: 'promoted_to_user',          label: 'Promoted → user' },
  { value: 'demoted_from_user',         label: 'Demoted from user' },
  { value: 'user_role_changed',         label: 'Role changed' },
  { value: 'app_disabled',              label: 'App disabled' },
  { value: 'app_enabled',               label: 'App enabled' },
  { value: 'access_policy_changed',     label: 'Access policy changed' },
  { value: 'm365_synced',               label: 'M365 synced' },
  { value: 'm365_promoted',             label: 'M365 promoted' },
  { value: 'contact_anonymized',        label: 'Contact anonymized' },
];

const ACTION_COLORS: Record<string, string> = {
  contact_created:        'bg-emerald-50 text-emerald-700 border-emerald-200',
  contact_updated:        'bg-blue-50 text-blue-700 border-blue-200',
  contact_deactivated:    'bg-amber-50 text-amber-700 border-amber-200',
  contact_reactivated:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  contact_merged:         'bg-purple-50 text-purple-700 border-purple-200',
  contact_deleted:        'bg-red-50 text-red-700 border-red-200',
  promoted_to_user:       'bg-indigo-50 text-indigo-700 border-indigo-200',
  demoted_from_user:      'bg-orange-50 text-orange-700 border-orange-200',
  user_role_changed:      'bg-blue-50 text-blue-700 border-blue-200',
  app_disabled:           'bg-amber-50 text-amber-700 border-amber-200',
  app_enabled:            'bg-emerald-50 text-emerald-700 border-emerald-200',
  access_policy_changed:  'bg-blue-50 text-blue-700 border-blue-200',
  m365_synced:            'bg-stone-100 text-stone-700 border-stone-200',
  m365_promoted:          'bg-stone-100 text-stone-700 border-stone-200',
  contact_anonymized:     'bg-red-50 text-red-700 border-red-200',
};

function formatTimestamp(ts: any): string {
  if (!ts) return '—';
  try {
    if (typeof ts.toDate === 'function') return ts.toDate().toLocaleString();
    return String(ts);
  } catch {
    return String(ts);
  }
}

const AuditLogPage: React.FC = () => {
  const { role, firebaseUser } = useAuth();
  const isSuperadmin = role === 'superadmin';

  const [logs, setLogs]               = useState<AuditLogRecord[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [filterAction, setFilterAction] = useState<AuditAction | ''>('');
  const [filterActor, setFilterActor] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [expandedId, setExpandedId]   = useState<string | null>(null);

  useEffect(() => {
    if (!isSuperadmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetchRecentAuditLogs(500)
      .then(setLogs)
      .catch((e) => setError(e?.message ?? String(e)))
      .finally(() => setLoading(false));
  }, [isSuperadmin]);

  const filtered = useMemo(() => {
    return logs.filter((l) => {
      if (filterAction && l.action !== filterAction) return false;
      if (filterActor && !l.performedBy?.includes(filterActor)) return false;
      if (filterSubject && l.subjectContactId !== filterSubject && l.subjectUid !== filterSubject) return false;
      return true;
    });
  }, [logs, filterAction, filterActor, filterSubject]);

  if (!isSuperadmin) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center space-y-3">
          <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
            <span className="material-symbols-rounded text-amber-500" style={{ fontSize: 28 }}>lock</span>
          </div>
          <h2 className="text-lg font-bold text-stone-800">Superadmin only</h2>
          <p className="text-sm text-stone-500">
            Audit log access is restricted. Your current role: <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs">{role ?? 'none'}</code>
          </p>
          <p className="text-xs text-stone-400">{firebaseUser?.email}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-stone-900">Audit Log</h1>
        <p className="text-sm text-stone-500 mt-0.5">
          Append-only history of all contact + user mutations. Last 500 entries.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value as AuditAction | '')}
          className="border border-stone-200 rounded-lg px-3 py-2.5 text-sm bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
        >
          {ACTION_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <input
          type="text"
          value={filterActor}
          onChange={(e) => setFilterActor(e.target.value)}
          placeholder="Filter by performedBy (uid)…"
          className="flex-1 min-w-48 border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
        />

        <input
          type="text"
          value={filterSubject}
          onChange={(e) => setFilterSubject(e.target.value)}
          placeholder="Filter by subject (contactId or uid)…"
          className="flex-1 min-w-48 border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
        />
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {[1,2,3,4,5,6].map((i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="h-5 w-32 bg-stone-200 rounded" />
                <div className="h-5 flex-1 bg-stone-100 rounded" />
                <div className="h-5 w-24 bg-stone-100 rounded" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-2">
            <span className="material-symbols-rounded text-stone-300" style={{ fontSize: 36 }}>history</span>
            <p className="text-sm text-stone-500">
              {logs.length === 0 ? 'No audit logs yet' : 'No logs match your filters'}
            </p>
            {logs.length === 0 && (
              <p className="text-xs text-stone-400 max-w-xs text-center">
                Logs are written automatically when contacts/users mutate via Account Portal.
              </p>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-100 bg-stone-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  When
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">
                  By
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">
                  App
                </th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {filtered.map((log) => {
                const expanded = expandedId === log.id;
                const colorClass = ACTION_COLORS[log.action] ?? 'bg-stone-100 text-stone-700 border-stone-200';
                return (
                  <React.Fragment key={log.id}>
                    <tr
                      onClick={() => setExpandedId(expanded ? null : log.id)}
                      className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-xs text-stone-500 whitespace-nowrap">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${colorClass}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs text-stone-700">
                          {log.subjectContactId ?? log.subjectUid ?? '—'}
                        </code>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <code className="text-xs text-stone-500">{log.performedBy}</code>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-xs text-stone-500">{log.performedFromApp}</span>
                      </td>
                      <td className="px-2 py-3 text-stone-400">
                        <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
                          {expanded ? 'expand_less' : 'expand_more'}
                        </span>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="bg-stone-50/50">
                        <td colSpan={6} className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            <div>
                              <p className="font-semibold text-stone-600 mb-1">BEFORE</p>
                              <pre className="bg-white border border-stone-200 rounded p-3 overflow-x-auto text-[11px] leading-relaxed">
                                {log.before ? JSON.stringify(log.before, null, 2) : '—'}
                              </pre>
                            </div>
                            <div>
                              <p className="font-semibold text-stone-600 mb-1">AFTER</p>
                              <pre className="bg-white border border-stone-200 rounded p-3 overflow-x-auto text-[11px] leading-relaxed">
                                {log.after ? JSON.stringify(log.after, null, 2) : '—'}
                              </pre>
                            </div>
                            {log.notes && (
                              <div className="md:col-span-2">
                                <p className="font-semibold text-stone-600 mb-1">NOTES</p>
                                <p className="bg-white border border-stone-200 rounded p-3 text-stone-700">
                                  {log.notes}
                                </p>
                              </div>
                            )}
                            <div className="md:col-span-2 text-[10px] text-stone-400">
                              Log ID: <code>{log.id}</code>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-[11px] text-stone-400 mt-3">
        Loaded {filtered.length}/{logs.length} entries. Per Firestore rules, only superadmin can read this collection.
      </p>
    </div>
  );
};

export default AuditLogPage;
